import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, Clock } from 'lucide-react';
export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const details = [{
    icon: <Phone className="w-5 h-5" data-id="element-203" />,
    label: 'Phone',
    value: '1-888-2AVIATE (228-4283)',
    href: 'tel:1-888-228-4283'
  }, {
    icon: <Mail className="w-5 h-5" data-id="element-204" />,
    label: 'Email',
    value: 'info@planesmart.com',
    href: 'mailto:info@planesmart.com'
  }, {
    icon: <MapPin className="w-5 h-5" data-id="element-205" />,
    label: 'Headquarters',
    value: 'Addison Airport (KADS), Dallas, TX'
  }, {
    icon: <Clock className="w-5 h-5" data-id="element-206" />,
    label: 'Availability',
    value: '24/7 Concierge Support'
  }];
  return <>
      {/* Page Hero */}
      <section className="relative flex items-center justify-center pt-40 pb-24" data-id="element-207">
        <div className="absolute inset-0 z-0" data-id="element-208">
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal-900/70 to-charcoal-900/90 z-10" data-id="element-209" />
          <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2500&auto=format&fit=crop" alt="Private jet wing over clouds" className="w-full h-full object-cover" data-id="element-210" />
        </div>
        <div className="relative z-10 text-center px-6" data-id="element-211">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-5xl md:text-6xl font-serif text-white mb-5 drop-shadow-lg" data-id="element-212">
            Let's Plan Your{' '}
            <span className="italic text-brand-300" data-id="element-213">Journey</span>
          </motion.h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto font-light drop-shadow-md" data-id="element-214">
            Our aviation specialists are available around the clock to craft
            your next flight. Reach out and we'll respond within the hour.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-brand-50" data-id="element-215">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-start" data-id="element-216">
          {/* Details */}
          <div data-id="element-217">
            <h2 className="text-sm font-bold tracking-widest text-brand-600 uppercase mb-4" data-id="element-218">
              Get In Touch
            </h2>
            <h3 className="text-4xl font-serif text-charcoal-900 mb-6" data-id="element-219">
              Speak with a private aviation expert
            </h3>
            <p className="text-gray-600 text-lg mb-10 leading-relaxed" data-id="element-220">
              Whether you're booking a charter, managing an aircraft, or
              exploring ownership, our team is ready to help you fly smarter.
            </p>

            <div className="grid sm:grid-cols-2 gap-6" data-id="element-221">
              {details.map(d => <div key={d.label} className="bg-white rounded-2xl p-6 border border-brand-100 shadow-sm" data-id="element-222">
                  <div className="w-11 h-11 rounded-xl bg-brand-100 text-brand-600 flex items-center justify-center mb-4" data-id="element-223">
                    {d.icon}
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1" data-id="element-224">
                    {d.label}
                  </p>
                  {d.href ? <a href={d.href} className="text-charcoal-900 font-medium hover:text-brand-600 transition-colors" data-id="element-225">
                      {d.value}
                    </a> : <p className="text-charcoal-900 font-medium" data-id="element-226">{d.value}</p>}
                </div>)}
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-glass border border-brand-100" data-id="element-227">
            {submitted ? <div className="text-center py-16" data-id="element-228">
                <div className="w-16 h-16 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center mx-auto mb-6" data-id="element-229">
                  <Send className="w-7 h-7" data-id="element-230" />
                </div>
                <h3 className="text-2xl font-serif text-charcoal-900 mb-3" data-id="element-231">
                  Message received
                </h3>
                <p className="text-gray-600" data-id="element-232">
                  Thank you for reaching out. A PlaneSmart specialist will be in
                  touch shortly.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-233">
                <h3 className="text-2xl font-serif text-charcoal-900 mb-2" data-id="element-234">
                  Send us a message
                </h3>
                <div className="grid sm:grid-cols-2 gap-5" data-id="element-235">
                  <div data-id="element-236">
                    <label className="block text-sm font-medium text-gray-600 mb-1.5" data-id="element-237">
                      Full name
                    </label>
                    <input type="text" required className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all" data-id="element-238" />
                  </div>
                  <div data-id="element-239">
                    <label className="block text-sm font-medium text-gray-600 mb-1.5" data-id="element-240">
                      Phone
                    </label>
                    <input type="tel" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all" data-id="element-241" />
                  </div>
                </div>
                <div data-id="element-242">
                  <label className="block text-sm font-medium text-gray-600 mb-1.5" data-id="element-243">
                    Email
                  </label>
                  <input type="email" required className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all" data-id="element-244" />
                </div>
                <div data-id="element-245">
                  <label className="block text-sm font-medium text-gray-600 mb-1.5" data-id="element-246">
                    How can we help?
                  </label>
                  <textarea rows={4} required className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all resize-none" data-id="element-247" />
                </div>
                <button type="submit" className="w-full bg-brand-600 hover:bg-brand-700 text-white py-4 rounded-xl font-medium transition-colors flex items-center justify-center gap-2" data-id="element-248">
                  Send Message
                  <Send className="w-4 h-4" data-id="element-249" />
                </button>
              </form>}
          </div>
        </div>
      </section>
    </>;
}